import {FormEvent, useState} from 'react';
import gql from 'graphql-tag';
import type {ExecutionResult} from '@apollo/react-common';
import {useMutation} from 'services/apollo';
import {EzPage, EzCard, EzHeading, EzField, EzLink} from '@ezcater/recipe';
import {RequestMagicLink} from 'services/graphql';
import {formatPhoneNumber} from 'util/StringUtils';
import Button from 'components/Button';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';
import queryString from 'query-string';
import {usePageViewTracking} from 'util/tracking';
import {EXPIRED_MAGIC_LINK} from 'util/tracking/trackingEvents';
import {
  DriverSelfAssignSource,
  SelfAssignSource,
  type ExpiredMagicLinkCreateMutation,
  type ExpiredMagicLinkCreateMutationVariables,
  type MagicLinkRequestMutation,
  type MagicLinkRequestMutationVariables,
} from 'services/graphql/types';
import {useFederationApiEppoFlag} from 'services/eppo';
import {TERMS_URL} from 'paths';
import Layout from '../Layout';

export const MagicLinkCreateMutation = gql(/* GraphQL */ `
  mutation ExpiredMagicLinkCreateMutation($input: DriverAccountMagicLinkCreateInput!) {
    magicLinkRequest: driverAccountMagicLinkCreate(input: $input) {
      userErrors {
        ... on UserError {
          message
        }
      }
    }
  }
`);

const getAdaptedSelfAssignSource = (
  source?: MagicLinkRequestMutationVariables['input']['source']
): DriverSelfAssignSource | undefined => {
  switch (source) {
    case SelfAssignSource.Qrcode: {
      return DriverSelfAssignSource.Qrcode;
    }
    case SelfAssignSource.Sms: {
      return DriverSelfAssignSource.Sms;
    }
    default:
      return undefined;
  }
};

type UseMagicLinkCreateMutationResult =
  | ReturnType<typeof useMutation<MagicLinkRequestMutation, MagicLinkRequestMutationVariables>>
  | [
      (variables: {
        variables: MagicLinkRequestMutationVariables;
      }) => Promise<ExecutionResult<ExpiredMagicLinkCreateMutation>>,
      ReturnType<
        typeof useMutation<ExpiredMagicLinkCreateMutation, ExpiredMagicLinkCreateMutationVariables>
      >[1],
    ];

const useMagicLinkCreateMutation = (): UseMagicLinkCreateMutationResult => {
  const featureFlag = useFederationApiEppoFlag();
  const [executeLegacyMutation, legacyMutationResult] = useMutation<
    MagicLinkRequestMutation,
    MagicLinkRequestMutationVariables
  >(RequestMagicLink);
  const [executeMutation, mutationResult] = useMutation<
    ExpiredMagicLinkCreateMutation,
    ExpiredMagicLinkCreateMutationVariables
  >(MagicLinkCreateMutation);

  if (featureFlag) {
    return [
      ({variables}: {variables: MagicLinkRequestMutationVariables}) =>
        executeMutation({
          variables: {
            input: {
              assignmentId: variables.input.driverAssignmentId,
              deliveryId: variables.input.deliveryId,
              phone: variables.input.phone,
              source: getAdaptedSelfAssignSource(variables.input.source),
            },
          },
        }),
      mutationResult,
    ];
  }

  return [executeLegacyMutation, legacyMutationResult];
};

const ExpiredMagicLink = () => {
  usePageViewTracking(EXPIRED_MAGIC_LINK);
  const history = useHistory();
  const {t} = useTranslation();
  const [phoneNumber, setPhoneNumber] = useState('');

  const {search} = history.location;
  const parsedQueryString = queryString.parse(search);
  const driverAssignmentId = parsedQueryString.driverAssignmentId as string | undefined;
  const deliveryId = parsedQueryString.deliveryId as string | undefined;
  const source = parsedQueryString.source as SelfAssignSource | undefined;

  const [magicLinkRequestQuery, magicLinkRequestData] = useMagicLinkCreateMutation();

  const onChange = (value: string) => {
    setPhoneNumber(value);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      await magicLinkRequestQuery({
        variables: {
          input: {
            phone: phoneNumber,
            driverAssignmentId,
            deliveryId,
            source,
          },
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const defaultError = t('expiredMagicLink.error');

  const getError = () => {
    if (magicLinkRequestData.error) {
      return defaultError;
    }

    if (magicLinkRequestData?.data?.magicLinkRequest?.userErrors?.length) {
      return magicLinkRequestData?.data?.magicLinkRequest?.userErrors[0].message;
    }
  };

  const title = t('expiredMagicLink.title');
  const defaultHeader = t('expiredMagicLink.header');
  const defaultSubheader = t('expiredMagicLink.subheader');
  const phoneNumberText = t('expiredMagicLink.phoneNumber');
  const phoneNumberPlaceholder = t('expiredMagicLink.phoneNumberPlaceholder');
  const buttonText = t('expiredMagicLink.button');
  const disclaimer = t('expiredMagicLink.disclaimer');
  const terms = t('expiredMagicLink.terms');
  const successHeader = t('expiredMagicLink.successHeader');
  const successSubheader = t('expiredMagicLink.successSubheader');

  const error = getError();
  const showSuccessMessages = !!magicLinkRequestData.data?.magicLinkRequest && !error;

  const header = showSuccessMessages ? successHeader : defaultHeader;
  const subheader = showSuccessMessages ? successSubheader : defaultSubheader;

  return (
    <Layout title={title}>
      <EzPage>
        <EzHeading size={'3'}>{header}</EzHeading>
        <EzHeading size={'4'}>{subheader}</EzHeading>
        {!showSuccessMessages && (
          <form onSubmit={handleSubmit}>
            <EzCard>
              {error && <>{error}</>}
              {!showSuccessMessages && (
                <>
                  <EzField
                    type="text"
                    label={phoneNumberText}
                    value={formatPhoneNumber(phoneNumber)}
                    onChange={e => onChange(e.currentTarget.value)}
                    placeholder={phoneNumberPlaceholder}
                  />
                  <Button
                    type="submit"
                    buttonType="primary"
                    buttonText={buttonText}
                    loading={magicLinkRequestData.loading}
                    disabled={phoneNumber === ''}
                  />

                  <EzHeading size="6">
                    {disclaimer}&nbsp;&nbsp;
                    <EzLink href={TERMS_URL} target="_blank">
                      {terms}
                    </EzLink>
                  </EzHeading>
                </>
              )}
            </EzCard>
          </form>
        )}
      </EzPage>
    </Layout>
  );
};

export default ExpiredMagicLink;
